import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Home, Key, TrendingUp, Settings } from 'lucide-react';
import { pickText } from '../lib/content';

export const ServicesSection = ({ t, content, lang = 'sq' }) => {
  const c = content?.services || {};
  const svcTitle = pickText(c.title, lang, t.services.title);
  const svcSubtitle = pickText(c.subtitle, lang, t.services.subtitle);

  const services = [
    {
      icon: Home,
      title: pickText(c.sales_title, lang, t.services.sales.title),
      description: pickText(c.sales_description, lang, t.services.sales.description),
      gradient: 'from-[#C5A05E] via-[#D4B06E] to-[#C5A05E]'
    },
    {
      icon: Key,
      title: pickText(c.rental_title, lang, t.services.rental.title),
      description: pickText(c.rental_description, lang, t.services.rental.description),
      gradient: 'from-[#D4B06E] via-[#C5A05E] to-[#B39050]'
    },
    {
      icon: TrendingUp,
      title: pickText(c.investment_title, lang, t.services.investment.title),
      description: pickText(c.investment_description, lang, t.services.investment.description),
      gradient: 'from-[#B39050] via-[#C5A05E] to-[#D4B06E]'
    },
    {
      icon: Settings,
      title: pickText(c.property_title, lang, t.services.property.title),
      description: pickText(c.property_description, lang, t.services.property.description),
      gradient: 'from-[#C5A05E] via-[#B39050] to-[#D4B06E]'
    }
  ];

  return (
    <section id="services" className="py-32 bg-gradient-to-b from-black via-slate-950 to-black relative overflow-hidden">
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 animate-pulse" style={{ animationDuration: '4s' }}>
          <div className="absolute inset-0" style={{
            backgroundImage: `
              radial-gradient(circle at 20% 50%, rgba(197, 160, 94, 0.3) 0%, transparent 50%),
              radial-gradient(circle at 80% 50%, rgba(197, 160, 94, 0.2) 0%, transparent 50%)
            `
          }} />
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20 space-y-6">
          <div className="inline-block px-6 py-2 bg-[#C5A05E]/10 backdrop-blur-sm border border-[#C5A05E]/30 rounded-full text-[#C5A05E] text-sm font-semibold uppercase tracking-wider mb-4 animate-fadeInUp">
            {svcTitle}
          </div>
          <h2 className="text-5xl md:text-7xl font-bold text-white animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
            {svcSubtitle}
          </h2>
          <div className="flex items-center justify-center gap-3 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <div className="h-px w-24 bg-gradient-to-r from-transparent to-[#C5A05E]" />
            <div className="w-2 h-2 bg-[#C5A05E] rounded-full animate-pulse" />
            <div className="h-px w-24 bg-gradient-to-l from-transparent to-[#C5A05E]" />
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="group relative animate-fadeInUp"
              style={{ animationDelay: `${0.3 + index * 0.1}s`, opacity: 0, animationFillMode: 'forwards' }}
            >
              {/* Glowing border effect */}
              <div className={`absolute -inset-1 bg-gradient-to-r ${service.gradient} rounded-3xl opacity-0 group-hover:opacity-100 blur-xl transition-all duration-500`} />
              
              <Card className="relative backdrop-blur-2xl bg-white/5 border border-white/10 hover:border-white/20 overflow-hidden transition-all duration-500 h-full">
                {/* Top gradient line */}
                <div className={`h-2 bg-gradient-to-r ${service.gradient}`} />
                
                <CardHeader>
                  <div className="flex items-start gap-6">
                    {/* Icon with animated background */}
                    <div className="relative flex-shrink-0">
                      <div className={`absolute inset-0 bg-gradient-to-r ${service.gradient} opacity-20 blur-xl group-hover:opacity-40 transition-opacity duration-500`} />
                      <div className={`relative w-16 h-16 rounded-2xl bg-gradient-to-r ${service.gradient} p-[2px] group-hover:scale-110 transition-transform duration-500`}>
                        <div className="w-full h-full bg-black/90 backdrop-blur-xl rounded-2xl flex items-center justify-center">
                          <service.icon className="w-8 h-8 text-[#C5A05E]" />
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex-1 pt-1">
                      <CardTitle className="text-2xl font-bold text-white group-hover:text-[#C5A05E] transition-colors duration-300">
                        {service.title}
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="px-8 pb-8">
                  <p className="text-gray-400 leading-relaxed text-lg">{service.description}</p>
                  
                  {/* Decorative bottom element */}
                  <div className="mt-6 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className={`h-px flex-1 bg-gradient-to-r ${service.gradient}`} />
                    <div className="w-2 h-2 bg-[#C5A05E] rounded-full" />
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};